// src/pages/ia-paca/metiers/consultant-ia.tsx
//
// Page métier — Consultant IA en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Target, Users, BarChart3, Workflow, Lightbulb, ShieldCheck } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'consultant-ia',
  metaTitle: 'Recrutement Consultant IA en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Consultant IA en PACA : profils mixtes tech + business, accompagnement transformation IA, pilotage POC, conduite du changement. Salaire 55-120k€.",
  metaKeywords:
    'recrutement Consultant IA, Consultant IA Marseille, Consultant IA Sophia, salaire Consultant IA 2026, recruter Consultant transformation IA PACA',
  heroLabel: 'Consultant IA',
  heroSubtitle:
    "Pas un slide-maker de cabinet généraliste qui découvre l'IA. Un consultant qui a vraiment piloté des projets IA en production, qui sait challenger un dirigeant, prioriser une roadmap, et embarquer des équipes terrain dans la durée.",
  heroBadges: ['Transformation IA', 'POC piloté', 'Change management', 'ROI'],
  jsonLdName: 'Consultant IA',
  jsonLdDescription:
    "Le Consultant IA accompagne les entreprises dans leur transformation IA : diagnostic de maturité, identification des cas d'usage, pilotage de POC, conduite du changement, structuration de l'équipe.",
  jsonLdSkills:
    'Diagnostic maturité IA, identification cas d\'usage, business case, pilotage POC, conduite du changement, formation, recrutement IA, AI Act, gouvernance, structuration équipe IA',
  jsonLdMinSalary: 55000,
  jsonLdMedianSalary: 80000,
  jsonLdMaxSalary: 120000,
  jsonLdEducation: 'Grande école d\'ingé / commerce, MBA, ou parcours mixte tech + conseil.',
  jsonLdExperience: '3-15+ ans, forte variance selon BU.',
  intro: {
    title: 'Consultant IA : le profil qui rend votre transformation réelle.',
    paragraphs: [
      "Un Consultant IA <strong>n'est utile que s'il a déjà mis les mains dans le cambouis</strong>. Sans ça, il sera un commentateur de présentations PowerPoint. Avec ça, il devient un accélérateur puissant qui peut faire gagner 6 à 12 mois à votre entreprise.",
      "Concrètement : il diagnostique votre maturité IA actuelle (souvent avec un outil type AI Readiness Score), identifie 3-5 cas d'usage prioritaires, monte les business cases, pilote 1 à 2 POC, structure votre gouvernance IA, et recrute (ou aide à recruter) les bons profils tech. Il sait aussi dire non à un POC séduisant mais non rentable.",
      "<strong>Le piège du marché :</strong> 70% des consultants IA en PACA n'ont jamais shippé un produit IA. Ils viennent du conseil généraliste et se sont reconvertis sur la vague IA. Notre filtre : nous cherchons des consultants qui ont au moins 1 référence client vérifiable avec un produit IA en production.",
    ],
  },
  stackTitle: 'Les compétences que votre Consultant IA doit avoir.',
  stack: [
    {
      Icon: Target,
      title: 'Cadrage stratégique',
      tools: ['Diagnostic maturité', 'Use case discovery', 'Priorisation (impact / effort)', 'Business case (ROI)', 'OKR IA'],
    },
    {
      Icon: Workflow,
      title: 'Pilotage POC',
      tools: ['Sizing POC', 'Sprint cadence', 'Definition of Done', 'Gates d\'arrêt', 'Industrialisation post-POC'],
    },
    {
      Icon: Users,
      title: 'Conduite du changement',
      tools: ['Stakeholder mapping', 'Communication exec', 'Formation utilisateurs', 'Coaching managers', 'Ambassadeurs IA'],
    },
    {
      Icon: ShieldCheck,
      title: 'Gouvernance & risques',
      tools: ['AI Act', 'RGPD IA', 'Charte interne', 'Comité IA', 'Plan de conformité'],
    },
    {
      Icon: Lightbulb,
      title: 'Compréhension tech IA',
      tools: ['LLM, RAG, fine-tuning', 'MLOps basics', 'Vector search', 'Coûts cloud IA', 'Eval & métriques'],
    },
    {
      Icon: BarChart3,
      title: 'Outils consultant',
      tools: ['Slides exec', 'Notion / Coda', 'Miro / Figma', 'Linear / Asana', 'Excel financier'],
    },
  ],
  salaryByCityTitle: '55-120 k€, forte variance selon contexte (cabinet vs interne, sénior vs lead).',
  salaryByCity: [
    { ville: 'Marseille', junior: '55 – 65 k€', confirme: '65 – 88 k€', senior: '88 – 115 k€', expert: '110 – 130 k€' },
    { ville: 'Aix-en-Provence', junior: '52 – 62 k€', confirme: '62 – 85 k€', senior: '85 – 110 k€', expert: '108 – 128 k€' },
    { ville: 'Sophia Antipolis', junior: '58 – 68 k€', confirme: '68 – 92 k€', senior: '92 – 120 k€', expert: '115 – 140 k€' },
    { ville: 'Nice', junior: '55 – 65 k€', confirme: '65 – 90 k€', senior: '90 – 118 k€', expert: '112 – 135 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Consultant IA.',
  questionsIntro:
    "Ces questions séparent un Consultant IA mature d'un Consultant généraliste qui a fait un MOOC IA.",
  questions: [
    {
      q: "Décris la dernière mission IA que tu as pilotée. Quels livrables, quels résultats mesurables ?",
      good:
        "Cite un cas concret : contexte client, périmètre mission, livrables (diagnostic, business cases, POC en prod, plan d'industrialisation), résultats mesurables (gain en heures, € de CA, taux d'adoption). Sait aussi expliquer les difficultés et comment il/elle les a résolues.",
      bad: "Reste en théorie ou cite des missions où le rôle se limitait à des slides. Pas de chiffres, pas d'apprentissage.",
    },
    {
      q: "Un comex te demande 'on doit faire du LLM'. Comment tu réagis ?",
      good:
        "Recadre poliment : 'LLM est une techno, pas un objectif'. Demande quel problème business. Propose un diagnostic rapide (2-3 semaines) pour identifier les cas d'usage où le LLM apporte vraiment de la valeur vs autres approches (règles métier, ML classique, automation classique). Sait dire 'pas besoin de LLM pour ça'.",
      bad: "Accepte la commande sans questionner. Propose direct un POC ChatGPT customisé. Renforce l'illusion 'IA = LLM'.",
    },
    {
      q: 'Comment tu mesures le succès d\'un POC IA ?',
      good:
        "Définit en amont 3 niveaux de KPI : 1) technique (modèle marche, accuracy/AUC), 2) usage (taux d'adoption utilisateurs, fréquence), 3) business (heures économisées, CA gagné, NPS amélioré). Définit des seuils minimaux pour décider go/no-go industrialisation. Pratique le 'kill switch' assumé.",
      bad: "'Si le modèle marche, on industrialise.' Pas de notion d'usage ni de business value.",
    },
    {
      q: "Sur la conduite du changement IA, quel framework tu utilises et pourquoi ?",
      good:
        "Cite un framework (ADKAR, Kotter, ou propre maison) adapté au contexte IA : focus sensibilisation, ambassadeurs, formation hands-on (pas slides), pilote contrôlé, scale progressif. Insiste sur la peur du remplacement et l'éthique d'usage interne.",
      bad: "'On forme tout le monde sur ChatGPT en 1h.' Sous-estime la résistance, casse de la confiance.",
    },
    {
      q: "Tu accompagnes une ETI qui n'a pas d'équipe IA. Comment tu structures la cible à 12 mois ?",
      good:
        "Pragmatique : commence par 1 sponsor exec + 1 Data Scientist senior (ou 1 AI Engineer si LLM-first) + budget POC. Évite de tout structurer d'un coup. Recrute au fil des besoins prouvés. Sait calibrer le mix interne / cabinet / freelance selon la maturité.",
      bad: "Propose une équipe de 10 personnes dès le mois 1. Sur-investit, échec garanti.",
    },
  ],
  ctaTitle: "Vous cherchez un Consultant IA en PACA ?",
  ctaSubtitle:
    "Profil souvent confondu avec un Consultant généraliste. Notre court-list filtre sur la pratique terrain. 4-6 candidats en 20-30 jours.",
};

export default function ConsultantIaPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
